import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

interface RevenueData {
  name: string;
  revenue: number;
  target: number;
  cumulative: number;
}

const data: RevenueData[] = [
  { name: 'Jan', revenue: 15000, target: 14000, cumulative: 15000 },
  { name: 'Feb', revenue: 18000, target: 16000, cumulative: 33000 },
  { name: 'Mar', revenue: 22000, target: 18000, cumulative: 55000 },
  { name: 'Apr', revenue: 25000, target: 20000, cumulative: 80000 },
  { name: 'May', revenue: 28000, target: 22000, cumulative: 108000 },
  { name: 'Jun', revenue: 32000, target: 24000, cumulative: 140000 },
];

const CustomXAxis = (props: any) => (
  <XAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

const CustomYAxis = (props: any) => (
  <YAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
    tickFormatter={(value) => `$${value/1000}k`}
  />
);

export function RevenueGrowth() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue Growth</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <CustomXAxis dataKey="name" />
              <CustomYAxis />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload?.length) {
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-sm">
                        <div className="text-sm font-medium">{label}</div>
                        <div className="mt-1 space-y-1">
                          {payload.map((entry) => (
                            <div
                              key={entry.name}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span className="text-muted-foreground">
                                {entry.name}: ${entry.value.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                name="Monthly Revenue"
                stroke="hsl(var(--primary))"
                fill="url(#colorRevenue)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="target"
                name="Target"
                stroke="hsl(var(--warning))"
                strokeDasharray="5 5"
                fill="none"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="cumulative"
                name="Cumulative Revenue"
                stroke="hsl(var(--success))"
                fill="url(#colorCumulative)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}